/**
 * Beni cart popover (mini cart).
 *
 * Implements the same add-to-cart contract as Dawn's <cart-notification> /
 * <cart-drawer> (getSectionsToRender / setActiveElement / renderContents) so the
 * existing add flows (assets/yg-script.js, snippets/yg-product-variant-kit-picker.liquid)
 * can drive it with no special-casing. Read-only: no line quantity editing.
 *
 * Opens on cart-icon click and automatically after an add-to-cart.
 */
class BeniCartPopover extends HTMLElement {
  constructor() {
    super();

    this.panel = this.querySelector('.beni-cart-popover__panel');
    this.activeElement = null;
    this.onBodyKeyup = this.handleKeyup.bind(this);

    // Delegated close: overlay, close button — survives content morphs.
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-popover-close]')) {
        event.preventDefault();
        this.close();
      }
    });

    this.setHeaderCartIconAccessibility();
  }

  // Mutual exclusion with the nav drawer. Only ever CLOSES the menu (from the
  // cart-icon click / open()); it never touches the menu or body state while the
  // menu is opening — doing that mid-open reflowed the header and misplaced the
  // drawer. Reuses Dawn's own close so it clears <details open>, the scrim,
  // overflow-hidden-* and the menu focus trap.
  closeMenu() {
    const headerDrawer = document.querySelector('header-drawer');
    if (headerDrawer && headerDrawer.querySelector('details[open]')) {
      headerDrawer.closeMenuDrawer(new Event('cart-open'), headerDrawer.querySelector('summary'));
    }
  }

  connectedCallback() {
    // Keep the mini-cart's contents current after ANY cart change (quantity
    // edit / remove on the cart page, adds from anywhere) so it's accurate the
    // next time it opens. This does NOT open the popover — only refreshes it.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && !this.cartUpdateUnsub) {
      this.cartUpdateUnsub = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => this.refreshFromEvent(event));
    }
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsub) this.cartUpdateUnsub();
    this.cartUpdateUnsub = null;
  }

  // Re-render #BeniCartPopover from the freshest cart HTML. Prefers the section
  // already present in the cart:update payload; otherwise fetches it.
  refreshFromEvent(event) {
    const inline = event && event.cartData && event.cartData.sections && event.cartData.sections['beni-cart-popover'];
    if (inline) {
      this.morphContents(inline);
      return;
    }
    const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
    fetch(`${cartUrl}?section_id=beni-cart-popover`)
      .then((r) => r.text())
      .then((html) => this.morphContents(html))
      .catch(() => {});
  }

  morphContents(sectionHtml) {
    const src = new DOMParser().parseFromString(sectionHtml, 'text/html').querySelector('#BeniCartPopover');
    const target = this.querySelector('#BeniCartPopover');
    if (!src || !target) return;
    target.innerHTML = src.innerHTML;
    const wrapper = new DOMParser().parseFromString(sectionHtml, 'text/html').querySelector('beni-cart-popover');
    if (wrapper && wrapper.dataset.empty) this.dataset.empty = wrapper.dataset.empty;
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    this.cartLink = cartLink;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');

    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE' || event.code === 'Enter') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  positionPanel() {
    const anchor = this.cartLink || document.querySelector('#cart-icon-bubble');
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();

    // Anchor the panel to the bottom of the whole header bar (not just the icon),
    // so the ~10px gap added in CSS reads from the header edge.
    const headerEl =
      anchor.closest('.header-wrapper, header, sticky-header, .section-header') || anchor.closest('.shopify-section');
    let headerBottom = rect.bottom;
    if (headerEl) {
      // When scrolled down the sticky header is transformed up off-screen
      // (`shopify-section-header-hidden` → translateY(-100%)), so its live
      // rect.bottom is negative/stale — this is what covered the panel on the
      // first add on iOS. Once revealed it pins at top:0, so its bottom equals
      // its own height; take the larger of the two to stay correct mid-animation.
      headerBottom = Math.max(headerEl.getBoundingClientRect().bottom, headerEl.offsetHeight);
    }

    this.style.setProperty('--bcp-top', `${Math.round(Math.max(headerBottom, 0))}px`);

    // Center the panel on the cart icon's midline (desktop). On mobile the CSS
    // media query forces a full-width sheet, so --bcp-right is ignored there.
    const viewportWidth = document.documentElement.clientWidth;
    const iconCenter = rect.left + rect.width / 2;
    const panelWidth = this.panel?.offsetWidth || 384;
    let right = viewportWidth - (iconCenter + panelWidth / 2);
    // Keep at least 16px from the viewport edge if centering would overflow.
    right = Math.min(Math.max(right, 16), viewportWidth - panelWidth - 16);
    this.style.setProperty('--bcp-right', `${Math.round(right)}px`);
  }

  open(triggeredBy) {
    // Close the nav drawer FIRST — before the already-active early-return. The
    // "cart → menu → cart" path leaves the popover active behind the open menu,
    // so returning early would close nothing; doing it here means a second
    // cart-icon tap dismisses the menu and reveals the cart (bug M01).
    this.closeMenu();
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);

    // Reveal the sticky header FIRST so the panel anchors under the visible bar.
    document.querySelector('sticky-header')?.reveal?.();
    this.positionPanel();

    // Timeout mirrors Dawn's drawer: guarantees the transition fires.
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    document.body.classList.add('overflow-hidden');
    document.body.addEventListener('keyup', this.onBodyKeyup);

    this.panel?.addEventListener(
      'transitionend',
      () => {
        if (typeof trapFocus === 'function') trapFocus(this.panel, this.panel);
      },
      { once: true }
    );
  }

  close() {
    this.classList.remove('active');
    document.body.classList.remove('overflow-hidden');
    document.body.removeEventListener('keyup', this.onBodyKeyup);

    if (typeof removeTrapFocus === 'function') removeTrapFocus(this.activeElement);
  }

  handleKeyup(event) {
    if (event.code === 'Escape') this.close();
  }

  renderContents(parsedState) {
    this.getSectionsToRender().forEach((section) => {
      const target = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (!target || !parsedState.sections?.[section.id]) return;
      target.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    this.dataset.empty = parsedState.item_count === 0 ? 'true' : 'false';
    this.panel = this.querySelector('.beni-cart-popover__panel');

    // On the cart page, adding a product must NOT pop the mini-cart: the page's
    // own <cart-items> refreshes itself via the cart:update pub-sub, so the
    // overlay would be redundant. We still refresh the popover contents + cart
    // bubble above (so it's current if opened later) — only skip auto-opening.
    if (document.body.classList.contains('template-cart')) return;

    this.open();
  }

  getSectionsToRender() {
    return [
      {
        id: 'beni-cart-popover',
        selector: '#BeniCartPopover',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('beni-cart-popover', BeniCartPopover);